import { mapState } from 'vuex';
import { TemplateArrayHelpers } from '@/utilities/mixins/TemplateArrayHelpers';
import {
  profiles as profilesApi,
  documents as documentsApi,
  itineraries as itinerariesApi,
  templateForms as templateFormsApi,
  projects as projectsApi
} from '../api';

export const FiltersMixin = {
  data: () => ({
    projectList: [],
    kindList: [],
    projectMemberOfNames: [],
    statusCountList: {
      document: [],
      creativity: [],
      itinerary: []
    },
    originalMemberOfNames: []
  }),

  mixins: [TemplateArrayHelpers],

  methods: {
    checkIfFilterExist(newFilter, filters) {
      const filterProperties = filters.map(filter => filter.property);
      return filterProperties.includes(newFilter.property);
    },

    async getProjectValues(
      entity,
      filters,
      subEntity,
      profileId,
      hideMainProject
    ) {
      const entities = {
        itinerary: itinerariesApi,
        document: documentsApi,
        profile: profilesApi,
        revision: profilesApi
      };

      const methods = {
        itinerary: 'projectValues',
        document: 'projectValues',
        profile: 'projectValues',
        revision: 'revisionsProjectValues'
      };

      const method = methods[entity];

      const payload = {
        sort: `project.template.list[0].name[${this.defaultLanguage}]`,
        order: 'asc',
        filter: []
      };

      if (entity === 'document') {
        const creativityFilter = {
          property: 'creativity',
          query: subEntity === 'creativity' ? true : false,
          condition: 'eq'
        };

        payload.filter.push(creativityFilter);
      }

      if (filters && filters.length > 0) {
        filters.forEach(filter => {
          if (filter.property === 'contentFilter') {
            payload['contentFilter'] = filter.query;
          } else {
            if (filter.property !== 'project.list') payload.filter.push(filter);
          }
        });
      }

      if (hideMainProject) payload['hideMainProject'] = true;

      if (profileId) payload['profileId'] = profileId;

      const res = await entities[entity][method](payload);

      const projectValues = res.list.map(project => {
        const { name, projectId } = project;
        const id = projectId.replace('#', '');
        const nameString = this.findTranslation(name, this.defaultLanguage);
        return { value: id, text: nameString };
      });

      const sortedValues = projectValues.sort((a, b) => {
        if (a.text < b.text) return -1;
        if (a.text > b.text) return 1;
        if (a.text == b.text) return 0;
      });

      this.projectList = [
        { text: this.$t('viewAllProjects'), value: '' },
        ...sortedValues
      ];
    },

    async getKindValues(entity, filters, subEntity, id) {
      const entities = {
        document: documentsApi,
        project: documentsApi,
        template: templateFormsApi,
        revision: profilesApi
      };

      const sort = {
        document: 'document.kind',
        revision: 'document.kind',
        project: 'document.kind',
        template: 'templateForm.kind'
      };

      const payload = {
        sort: sort[entity],
        order: 'asc',
        filter: []
      };

      if (id) {
        if (entity === 'project') {
          payload['projectId'] = id;
        } else {
          payload['profileId'] = id;
        }
      }

      if (
        entity === 'document' ||
        (entity === 'project' &&
          (subEntity === 'document' || subEntity === 'creativity'))
      ) {
        const creativityFilter = {
          property: 'creativity',
          query: subEntity === 'creativity' ? true : false,
          condition: 'eq'
        };

        payload.filter.push(creativityFilter);
      }

      if (filters && filters.length > 0) {
        filters.forEach(filter => {
          if (filter.property === 'contentFilter') {
            payload['contentFilter'] = filter.query;
          } else if (filter.property !== 'project.list') {
            payload.filter.push(filter);
          } else {
            payload['projectId'] = filter.query;
          }
        });
      }

      const res = await entities[entity].kindValues(payload);

      const kinds = res.list.map(kind => {
        return { text: kind, value: kind };
      });

      this.kindList = [{ text: this.$t('viewAllKinds'), value: '' }, ...kinds];
    },

    async getMemberOfNames(projectId, filters) {
      const payload = {};

      if (filters && filters.length > 0) {
        filters.forEach(filter => {
          if (filter.property === 'contentFilter')
            payload['contentFilter'] = filter.query;
        });
      }
      const res = await projectsApi.getMemberOfNames(projectId, payload);

      this.originalMemberOfNames = res.list;

      const names = res.list.flat().map(name => {
        const nameInDefaultLanguage = this.findTranslation(
          name,
          this.defaultLanguage
        );
        return { text: nameInDefaultLanguage, value: nameInDefaultLanguage };
      });

      this.projectMemberOfNames = [
        { text: this.$t('viewAllTags'), value: '' },
        ...names
      ];
    },

    async getDocumentStatusCount(entity, filters, id, subEntity) {
      const entities = {
        project: projectsApi,
        document: documentsApi,
        myProjectsCreativities: profilesApi,
        profile: profilesApi
      };

      const payload = {
        filter: []
      };

      if (filters && filters.length > 0) {
        filters.forEach(filter => {
          if (filter.property === 'contentFilter') {
            payload['contentFilter'] = filter.query;
          } else if (filter.property === 'project.list') {
            payload['projectId'] = filter.query;
          } else if (
            filter.property !== 'status' &&
            filter.property !== 'archived'
          ) {
            payload.filter.push(filter);
          }
        });
      }

      let res;

      if (entity === 'myProjectsCreativities') {
        res = await entities[entity].getDocumentStatusCountCreativitiesProjects(
          id,
          this.defaultLanguage,
          payload
        );
      } else {
        res = await entities[entity].getDocumentStatusCount(
          id,
          this.defaultLanguage,
          payload
        );
      }

      const availableStatus = {
        REJECTED: 'denied',
        CREATED: 'drafts',
        ACCEPTED: 'acceptedUpperCase',
        SUBMITTED: 'underReview',
        PREPUBLISHED: 'prePublished',
        PUBLISHED: 'published',
        COMPLETED: 'pendingApproval'
      };

      const statusCount = {
        PUBLISHED: 0,
        CREATED: 0,
        SUBMITTED: 0,
        ACCEPTED: 0,
        COMPLETED: 0,
        PREPUBLISHED: 0,
        REJECTED: 0
      };

      const statusHaveArchived = status =>
        status.hasOwnProperty('archived') && status.archived > 0;

      const hasArchived = res.some(statusHaveArchived);

      if (hasArchived) {
        availableStatus['ARCHIVED'] = 'archivedUpperCase';
        statusCount['ARCHIVED'] = 0;
      }

      res.forEach(status => {
        statusCount[status.status] = status?.notArchived
          ? status.notArchived
          : 0;

        if (hasArchived && status.hasOwnProperty('archived')) {
          statusCount.ARCHIVED = statusCount.ARCHIVED + status.archived;
        }
      });

      const statusKeys = Object.keys(availableStatus);

      const mappedStatus = statusKeys.map(key => {
        return {
          text: this.$t(availableStatus[key]),
          count: statusCount[key],
          value: key
        };
      });

      this.statusCountList[subEntity] = mappedStatus;
    },

    async getItineraryStatusCount(entity, filters, subEntity) {
      const profileId = this.userProfile?.profile?.profileId.replace('#', '');

      const entities = {
        profile: profilesApi
      };

      const payload = {
        filter: []
      };

      if (filters && filters.length > 0) {
        filters.forEach(filter => {
          if (filter.property === 'contentFilter') {
            payload['contentFilter'] = filter.query;
          } else if (filter.property === 'project.list') {
            payload['projectId'] = filter.query;
          } else if (
            filter.property !== 'status' &&
            filter.property !== 'archived'
          ) {
            payload.filter.push(filter);
          }
        });
      }

      const res = await entities[entity].getItineraryStatusCount(
        profileId,
        payload
      );

      const availableStatus = {
        CREATED: 'drafts',
        PUBLISHED: 'published'
      };

      const statusCount = {
        PUBLISHED: 0,
        CREATED: 0
      };

      const statusHaveArchived = status =>
        status.hasOwnProperty('archived') && status.archived > 0;

      const hasArchived = res.some(statusHaveArchived);

      if (hasArchived) {
        availableStatus['ARCHIVED'] = 'archivedUpperCase';
        statusCount['ARCHIVED'] = 0;
      }

      res.forEach(status => {
        statusCount[status.status] = status?.notArchived
          ? status.notArchived
          : 0;

        if (hasArchived && status.hasOwnProperty('archived')) {
          statusCount.ARCHIVED = statusCount.ARCHIVED + status.archived;
        }
      });

      const statusKeys = Object.keys(availableStatus);

      const mappedStatus = statusKeys.map(key => {
        return {
          text: this.$t(availableStatus[key]),
          count: statusCount[key],
          value: key
        };
      });

      this.statusCountList[subEntity] = mappedStatus;
    }
  },

  computed: {
    ...mapState({
      defaultLanguage: state => state.defaultLanguage,
      userProfile: state => state.authentication.userProfile
    })
  },

  watch: {
    defaultLanguage(newVal) {
      if (this.originalMemberOfNames.length > 0) {
        const names = this.originalMemberOfNames.flat().map(name => {
          const nameInDefaultLanguage = this.findTranslation(name, newVal);
          return { text: nameInDefaultLanguage, value: nameInDefaultLanguage };
        });

        this.projectMemberOfNames = [
          { text: this.$t('viewAllTags'), value: '' },
          ...names
        ];
      }
    }
  }
};
